import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, IndianRupee, Briefcase, GraduationCap, Clock, Users, Calendar,
  Share2, MessageCircle, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import PublicLayout from "@/components/layout/PublicLayout";
import ApplyModal from "@/components/ApplyModal";
import SaveJobButton from "@/components/SaveJobButton";
import api, { trackEvent } from "@/lib/api";
import { useSite, waLink } from "@/context/SiteContext";

export default function JobDetail() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  useEffect(() => {
    setJob(null); setNotFound(false);
    api.get(`/jobs/slug/${slug}`).then((r) => { setJob(r.data); trackEvent("job_view", { job: slug }); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <PublicLayout><div className="pt-40 pb-20 text-center"><h1 className="font-display text-2xl font-bold">Job not found</h1>
      <Link to="/jobs" className="text-brand-emerald mt-4 inline-block">← Back to all jobs</Link></div></PublicLayout>
  );
  if (!job) return <PublicLayout><div className="pt-40 grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-brand-emerald border-t-transparent animate-spin" /></div></PublicLayout>;

  const salary = job.salary_min && job.salary_max ? `₹${job.salary_min.toLocaleString()} – ₹${job.salary_max.toLocaleString()}` : job.salary || "Negotiable";
  const waMsg = `Hi Dreamzz HR & Consultancy, I am interested in the ${job.title} position.`;

  const jobSchema = {
    "@context": "https://schema.org", "@type": "JobPosting", title: job.title,
    description: job.description, datePosted: job.created_at,
    employmentType: (job.employment_type || "").toUpperCase().replace("-", "_"),
    hiringOrganization: { "@type": "Organization", name: settings.company_name },
    jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: job.location, addressRegion: "Karnataka", addressCountry: "IN" } },
    ...(job.salary_min ? { baseSalary: { "@type": "MonetaryAmount", currency: "INR", value: { "@type": "QuantitativeValue", minValue: job.salary_min, maxValue: job.salary_max, unitText: "MONTH" } } } : {}),
    industry: job.industry,
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: job.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (e) {}
  };

  const Meta = ({ icon: Icon, label, value }) => value ? (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400"><Icon size={15} className="text-brand-emerald" />{label}</div>
      <div className="font-display font-semibold mt-1">{value}</div>
    </div>
  ) : null;

  const List = ({ title, items }) => items?.length ? (
    <div className="mt-8">
      <h3 className="font-display font-bold text-lg mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((it, i) => <li key={i} className="flex items-start gap-2 text-slate-300"><CheckCircle2 size={17} className="text-brand-emerald mt-0.5 shrink-0" />{it}</li>)}
      </ul>
    </div>
  ) : null;

  return (
    <PublicLayout waMessage={waMsg}>
      <Seo title={job.title} description={job.description?.slice(0, 155)}
        schema={[jobSchema, breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Jobs", url: "/jobs" }, { name: job.title, url: window.location.href }])]} type="article" />

      <section className="pt-28 pb-6 section-radial border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
            <Link to="/" className="hover:text-brand-emerald">Home</Link><ChevronRight size={12} />
            <Link to="/jobs" className="hover:text-brand-emerald">Jobs</Link><ChevronRight size={12} />
            <span className="text-slate-300">{job.title}</span>
          </nav>
          <span className="eyebrow">{job.industry}</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">{job.title}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><MapPin size={16} className="text-brand-emerald" />{job.location}</span>
            <span className="flex items-center gap-1.5"><IndianRupee size={16} className="text-brand-emerald" />{salary}</span>
            <span className="flex items-center gap-1.5"><Briefcase size={16} className="text-brand-emerald" />{job.experience}</span>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button data-testid="job-apply-btn" size="lg" onClick={() => setApplyOpen(true)} className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold glow-primary">Apply Now</Button>
            <a href={waLink(settings.whatsapp, waMsg)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { job: job.slug })}>
              <Button size="lg" variant="outline" className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"><MessageCircle size={18} className="mr-2" />WhatsApp Apply</Button>
            </a>
            <Button size="lg" variant="ghost" onClick={share} data-testid="job-share-btn" className="text-slate-300"><Share2 size={18} className="mr-2" />Share</Button>
            <SaveJobButton slug={job.slug} showLabel className="px-4 py-2 rounded-lg glass" />
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Meta icon={Briefcase} label="Employment Type" value={job.employment_type} />
          <Meta icon={GraduationCap} label="Qualification" value={job.qualification || job.education} />
          <Meta icon={Users} label="Vacancies" value={job.vacancies} />
          <Meta icon={Clock} label="Working Hours" value={job.working_hours} />
          <Meta icon={Calendar} label="Application Deadline" value={job.deadline} />
        </div>

        {job.skills?.length > 0 && (
          <div className="mt-8">
            <h3 className="font-display font-bold text-lg mb-3">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {job.skills.map((s) => <span key={s} className="px-3 py-1.5 rounded-full bg-brand-emerald/15 border border-brand-emerald/30 text-brand-emerald text-sm">{s}</span>)}
            </div>
          </div>
        )}

        <div className="mt-8">
          <h3 className="font-display font-bold text-lg mb-3">Job Description</h3>
          <p className="text-slate-300 leading-relaxed whitespace-pre-line">{job.description}</p>
        </div>
        <List title="Responsibilities" items={job.responsibilities} />
        <List title="Requirements" items={job.requirements} />
        <List title="Benefits" items={job.benefits} />

        <div className="mt-10 glass-strong rounded-2xl p-6 text-center">
          <h3 className="font-display font-bold text-xl">Interested in this role?</h3>
          <p className="text-slate-400 mt-2">Apply now and our recruitment team will get in touch with you.</p>
          <Button data-testid="job-apply-btn-bottom" size="lg" onClick={() => setApplyOpen(true)} className="mt-5 bg-brand-emerald hover:bg-[#C62828] text-white font-semibold">Apply Now</Button>
        </div>
      </section>

      <ApplyModal job={job} open={applyOpen} onOpenChange={setApplyOpen} />
    </PublicLayout>
  );
}
